import { defineMessage, MessageDescriptor } from 'react-intl';
import { faLightbulb } from '@fortawesome/pro-regular-svg-icons/faLightbulb';
import { faMessage } from '@fortawesome/pro-regular-svg-icons/faMessage';
import { faListUl } from '@fortawesome/pro-solid-svg-icons/faListUl';
import { faFileCheck } from '@fortawesome/pro-light-svg-icons';
import {
  textBlue,
  textLavender,
  textOrange700,
  textPink700,
} from '@osmosis/tailwind-ui/styles/text';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { OAIMode } from './oai';

export type PromptId = 'introduce' | 'summarize' | 'questions' | 'tutor';
export interface ValueItem {
  key: string;
  id: string;
  defaultMessage: string;
}

export type PromptDefinition = {
  id: PromptId;
  label: MessageDescriptor;
  prompt: MessageDescriptor;
  description: MessageDescriptor;
  mode: OAIMode;
  icon: {
    icon: IconDefinition;
    color: string;
  };
};

export const EXAMPLE_PROMPTS: PromptDefinition[] = [
  {
    id: 'tutor',
    label: defineMessage({
      id: 'ai-panel.tutor-me',
      defaultMessage: 'Tutor me on this',
    }),
    prompt: defineMessage({
      id: 'ai-panel.tutor-prompt',
      defaultMessage: 'Tutor me on this topic',
    }),
    description: defineMessage({
      id: 'ai-panel.logged-out.card.tutor.desc',
      defaultMessage: 'Build clinical reasoning, Socratic-style.',
    }),
    mode: OAIMode.Tutor,
    icon: {
      icon: faMessage,
      color: textBlue,
    },
  },
  {
    id: 'introduce',
    label: defineMessage({
      id: 'ai-panel.explain-simply',
      defaultMessage: 'Explain this simply',
    }),
    prompt: defineMessage({
      id: 'ai-panel.introduce',
      defaultMessage: `Use this topic. Pick the best outline. Explain the topic simply for a 1st-time-learner in 6 high-yield bullets.
Each bullet = Label: 1 sentence, ≤18 words.`,
    }),
    description: defineMessage({
      id: 'ai-panel.logged-out.card.introduce.desc',
      defaultMessage: 'Get clearer explanations of difficult concepts.',
    }),
    mode: OAIMode.DefaultStudy,
    icon: {
      icon: faLightbulb,
      color: textPink700,
    },
  },
  {
    id: 'summarize',
    label: defineMessage({
      id: 'ai-panel.summarize-high-yield',
      defaultMessage: 'Summarize high-yield points',
    }),
    prompt: defineMessage({
      id: 'ai-panel.summarize',
      defaultMessage: `Summarize this topic in 8 high-yield bullets using these labels: Definition; Why it matters; Core idea;
Key details; Clinical/exam relevance; High-yield facts with 3 numbered items.`,
    }),
    description: defineMessage({
      id: 'ai-panel.logged-out.card.summarize.desc',
      defaultMessage: 'Review what matters most for exams.',
    }),
    mode: OAIMode.DefaultStudy,
    icon: {
      icon: faListUl,
      color: textLavender,
    },
  },
  {
    id: 'questions',
    label: defineMessage({
      id: 'ai-panel.quiz-questions',
      defaultMessage: 'Quiz me with board-style questions',
    }),
    prompt: defineMessage({
      id: 'ai-panel.quiz',
      defaultMessage: `Create 3 board-style, one-best-answer questions on this topic.
Use short clinical vignettes that test application, not simple recall.
`,
    }),
    description: defineMessage({
      id: 'ai-panel.logged-out.card.questions.desc',
      defaultMessage: 'Practice active recall for exam prep after watching.',
    }),
    mode: OAIMode.DefaultStudy,
    icon: {
      icon: faFileCheck,
      color: textOrange700,
    },
  },
];

export const LOCKED_VALUE_ITEMS: ValueItem[] = [
  {
    key: 'watch-video',
    id: 'ai-panel.logged-out.value.watch-video',
    defaultMessage: 'Watch the full video',
  },
  {
    key: 'ask',
    id: 'ai-panel.logged-out.value.ask',
    defaultMessage: 'Ask Osmosis AI questions',
  },
  {
    key: 'practice',
    id: 'ai-panel.logged-out.value.practice',
    defaultMessage: 'Generate practice questions',
  },
  {
    key: 'tutor',
    id: 'ai-panel.logged-out.value.tutor',
    defaultMessage: 'Get 1-on-1 AI tutoring',
  },
  {
    key: 'schedule',
    id: 'ai-panel.logged-out.value.schedule',
    defaultMessage: 'Create a personalized study schedule',
  },
];

export const ANALYTICS_LABELS = Object.fromEntries(
  EXAMPLE_PROMPTS.map((p) => [p.id, p.label.defaultMessage]),
) as Record<PromptId, string>;
